import { useState, useCallback, useRef, useEffect, useMemo, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  useWindowDimensions,
  Animated,
  Platform,
  Pressable,
} from 'react-native';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { GoalsSkeleton } from '../../components/Skeleton';
import { useRoleNavigation } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { risksApi, goalsApi } from '../../services/apiService';
import { useRisksQuery } from '../../hooks/queries';
import { OrganizationFilterDropdown, useCanViewAllOrgs } from '../../components/OrganizationFilterDropdown';
import { queryKeys } from '../../providers/QueryProvider';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { RiskList, StrategicGoalList } from '../../types';

type NavigationProp = NativeStackNavigationProp<any>;

// Icons
const ChevronDownIcon = ({ size = 14, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronLeftIcon = ({ size = 14, color = '#64748B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 14, color = '#64748B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 14, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 16, color = '#EF4444' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Edit icon (pencil)
const EditIcon = ({ size = 18, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Extended risk interface with related name
interface RiskWithGoal extends RiskList {
  strategic_goal_name?: string;
  strategic_goal_id?: number;
}

// Group risks by their related entity (strategic goal, initiative, activity,
// process, or service). `key` is the stable React key; `goalId` stays for
// strategic-goal groups (back-compat with goal-specific handlers).
interface RiskGroup {
  key: string;
  goalId: number;
  goalName: string;
  organizationName?: string;
  risks: RiskWithGoal[];
}

// Check icon for toast
const CheckIcon = ({ size = 20, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

export default function RisksScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute();
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 768;
  const { isViewOnly, canViewAllOrganizations } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme colors for dark mode
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
    sectionBg: isDark ? colors.backgroundAlt : '#F1F5F9',
  };

  const [refreshing, setRefreshing] = useState(false);

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastOpacity = useRef(new Animated.Value(0)).current;

  // Filters
  const [filterGoal, setFilterGoal] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const goalBtnRef = useRef<any>(null);
  const typeBtnRef = useRef<any>(null);
  const levelBtnRef = useRef<any>(null);

  const openDropdown = (ref: any, setter: (v: boolean) => void) => {
    if (ref.current) {
      ref.current.measureInWindow((x: number, y: number, w: number, h: number) => {
        setDropdownPosition({ top: y + h + 4, left: x, width: w });
        setter(true);
      });
    }
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const queryClient = useQueryClient();

  // Main risks query — React Query renders cached data instantly on focus.
  const {
    data: risksResponse,
    isLoading: loading,
    refetch,
  } = useRisksQuery({
    page: currentPage,
    pageSize,
    risk_type: filterType !== 'all' ? filterType : undefined,
    risk_level: filterLevel !== 'all' ? filterLevel : undefined,
    related_id: filterGoal !== 'all' ? filterGoal : undefined,
    related_type: filterGoal !== 'all' ? 'strategic_goal' : undefined,
    department_id: selectedOrgId ?? undefined,
    year: selectedYear,
  });

  // Goals list (for filter dropdown + grouping) — cached longer, rarely changes.
  const { data: goalsDropdownData } = useQuery({
    queryKey: ['goals', 'dropdown', 'risks'],
    queryFn: () => goalsApi.list({ page_size: 100 }),
    staleTime: 10 * 60 * 1000,
  });
  const goals: StrategicGoalList[] = goalsDropdownData?.results || [];

  // Derive grouped risks from the two queries. Re-runs only when the upstream
  // data changes — no manual state juggling needed.
  const riskGroups: RiskGroup[] = useMemo(() => {
    const risks: RiskWithGoal[] = risksResponse?.results || [];
    const groupMap = new Map<string, RiskGroup>();

    risks.forEach((risk) => {
      if (risk.related_type === 'strategic_goal') {
        const goalId = risk.related_id;
        const goal = goals.find((g) => g.id === goalId);
        const goalName =
          risk.related_name ||
          goal?.title ||
          t('goals.strategicGoalNumber', { id: goalId });

        const key = `strategic_goal-${goalId}`;
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            key,
            goalId,
            goalName,
            organizationName: risk.organization_name || undefined,
            risks: [],
          });
        }
        groupMap.get(key)!.risks.push({
          ...risk,
          strategic_goal_name: goalName,
          strategic_goal_id: goalId,
        });
      } else {
        // Risks linked to initiative / activity / process / service. Previously
        // these were dropped (only strategic_goal was grouped), so a created
        // risk never showed on the page. Group them by their related entity,
        // headed by related_name from the API.
        const key = `${risk.related_type}-${risk.related_id}`;
        const header =
          risk.related_name ||
          `${risk.related_type_display || risk.related_type} #${risk.related_id}`;
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            key,
            goalId: risk.related_id,
            goalName: header,
            organizationName: risk.organization_name || undefined,
            risks: [],
          });
        }
        groupMap.get(key)!.risks.push(risk);
      }
    });

    return Array.from(groupMap.values());
  }, [risksResponse, goals, t]);

  const totalCount = risksResponse?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Back-compat shim for handlers that called fetchData().
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
  }, [queryClient]);

  // Show toast notification
  const showSuccessToast = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    toastOpacity.setValue(0);

    // useNativeDriver is not supported on web
    const useNativeDriver = Platform.OS !== 'web';

    // Fade in
    Animated.timing(toastOpacity, {
      toValue: 1,
      duration: 300,
      useNativeDriver,
    }).start();

    // Auto hide after 3 seconds
    setTimeout(() => {
      Animated.timing(toastOpacity, {
        toValue: 0,
        duration: 300,
        useNativeDriver,
      }).start(() => {
        setShowToast(false);
      });
    }, 3000);
  }, [toastOpacity]);

  // Refresh on focus — cached data renders instantly, refetch runs silently.
  useFocusEffect(
    useCallback(() => {
      refetch();

      // Check for success message from navigation params
      const params = route.params as { successMessage?: string } | undefined;
      if (params?.successMessage) {
        showSuccessToast(params.successMessage);
        // Clear the param to avoid showing again
        navigation.setParams({ successMessage: undefined });
      }
    }, [refetch, route.params, showSuccessToast, navigation])
  );

  // Reset to page 1 when year changes; query key change triggers a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedOrgId]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const applyFilters = () => {
    setShowGoalDropdown(false);
    setShowTypeDropdown(false);
    setShowLevelDropdown(false);
    // Reset to first page. Filter state changes already flow through the
    // query key so React Query picks them up on the next render.
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterType('all');
    setFilterLevel('all');
    setFilterGoal('all');
    setSelectedOrgId(null);
    setShowGoalDropdown(false);
    setShowTypeDropdown(false);
    setShowLevelDropdown(false);
    // Reset to first page - fetchData will be called due to dependency change
    setCurrentPage(1);
  };

  const hasActiveFilters = filterType !== 'all' || filterLevel !== 'all' || filterGoal !== 'all' || selectedOrgId != null;

  // Risk level badge colors (solid backgrounds with white text per Figma)
  const getRiskLevelBadgeColor = (level: string): string => {
    switch (level) {
      case 'critical': return '#EF4444'; // Red
      case 'high': return '#EF4444'; // Red
      case 'medium': return '#FF7F0E'; // Orange (Figma chart/legacy/chart-2)
      case 'low': return '#22C55E'; // Green
      default: return '#9CA3AF';
    }
  };

  // Risk type badge color (purple per Figma)
  const getRiskTypeBadgeColor = (): string => {
    return '#9467BD'; // Purple (Figma chart/legacy/chart-5)
  };

  const getRiskTypeLabel = (type: string): string => {
    switch (type) {
      case 'strategic': return t('risks.typeStrategic');
      case 'operational': return t('risks.typeOperational');
      case 'financial': return t('risks.typeFinancial');
      case 'compliance': return t('risks.typeCompliance');
      default: return type;
    }
  };

  const getRiskLevelLabel = (level: string): string => {
    switch (level) {
      case 'critical': return t('risks.levelCritical');
      case 'high': return t('risks.levelHigh');
      case 'medium': return t('risks.levelMedium');
      case 'low': return t('risks.levelLow');
      default: return level;
    }
  };

  // Full-page skeleton only on cold load (no cached data yet). Warm cache
  // and background refetches fall through to the regular render.
  if (loading && riskGroups.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />

      {/* Success Toast Notification */}
      {showToast && (
        <Animated.View style={[styles.toast, { opacity: toastOpacity }]}>
          <CheckIcon size={20} color="#FFFFFF" />
          <Text style={styles.toastText}>{toastMessage}</Text>
        </Animated.View>
      )}

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home'), route: 'Dashboard' },
            { label: t('navigation.risks') },
          ]}
        />

        {/* Header Card */}
        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder, overflow: 'visible' }]}>
          <View style={styles.headerRow}>
            <Text style={[styles.pageTitle, { color: themeColors.textPrimary }]}>{t('risks.title')}</Text>
            {/* Add Risk button — open to ALL users (risk identification is
                not role-gated; product decision 2026-06). */}
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => navigation.navigate('AddRisk')}
            >
              <PlusIcon size={14} color="#FFFFFF" />
              <Text style={styles.addButtonText}>{t('risks.addRisk')}</Text>
            </TouchableOpacity>
          </View>

          {/* Filter Row - inside headerCard so it renders in the same div as the title */}
          <View style={styles.filterRowWrapper}>
          <View style={styles.filterRow}>
          {/* إزالة الفلتر button - only show when filters are active */}
          {hasActiveFilters && (
            <TouchableOpacity style={styles.clearFilterButton} onPress={clearFilters}>
              <XIcon size={14} color="#EF4444" />
              <Text style={styles.clearFilterText}>{t('filters.clearFilter')}</Text>
            </TouchableOpacity>
          )}

          {/* Apply Filter button */}
          <TouchableOpacity style={[styles.applyFilterBtn, { backgroundColor: themeColors.sectionBg }]} onPress={applyFilters}>
            <Text style={[styles.applyFilterBtnText, { color: themeColors.textPrimary }]}>{t('common.applyFilter')}</Text>
          </TouchableOpacity>

          {/* All Levels dropdown */}
          <View style={styles.filterDropdownContainer} ref={levelBtnRef}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => {
                setShowTypeDropdown(false);
                setShowGoalDropdown(false);
                openDropdown(levelBtnRef, (v) => setShowLevelDropdown(v));
              }}
            >
              <ChevronDownIcon size={14} color={themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: themeColors.textPrimary }]}>
                {filterLevel === 'all' ? t('risks.allLevels') : getRiskLevelLabel(filterLevel)}
              </Text>
            </TouchableOpacity>
          </View>

          {/* All Types dropdown */}
          <View style={styles.filterDropdownContainer} ref={typeBtnRef}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => {
                setShowLevelDropdown(false);
                setShowGoalDropdown(false);
                openDropdown(typeBtnRef, (v) => setShowTypeDropdown(v));
              }}
            >
              <ChevronDownIcon size={14} color={themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: themeColors.textPrimary }]}>
                {filterType === 'all' ? t('risks.allTypes') : getRiskTypeLabel(filterType)}
              </Text>
            </TouchableOpacity>
          </View>

          {/* All Goals dropdown */}
          <View style={styles.filterDropdownContainer} ref={goalBtnRef}>
            <TouchableOpacity
              style={[styles.filterDropdown, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              onPress={() => {
                setShowLevelDropdown(false);
                setShowTypeDropdown(false);
                openDropdown(goalBtnRef, (v) => setShowGoalDropdown(v));
              }}
            >
              <ChevronDownIcon size={14} color={themeColors.textSecondary} />
              <Text style={[styles.filterDropdownText, { color: themeColors.textPrimary }]} numberOfLines={1}>
                {filterGoal === 'all' ? t('risks.allGoals') : translate(goals.find(g => g.id.toString() === filterGoal)?.title) || filterGoal}
              </Text>
            </TouchableOpacity>
          </View>

          {/* Organization filter - last child = rightmost in RTL = first reading position */}
          {canViewAllOrgs && (
            <View style={styles.filterDropdownContainer}>
              <OrganizationFilterDropdown
                selectedOrgId={selectedOrgId}
                onChange={setSelectedOrgId}
              />
            </View>
          )}
        </View>
        </View>
        </View>

        {/* Dropdown portals - rendered outside all stacking contexts */}
        {Platform.OS === 'web' && (showLevelDropdown || showTypeDropdown || showGoalDropdown) && createPortal(
          <>
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }} onClick={() => { setShowLevelDropdown(false); setShowTypeDropdown(false); setShowGoalDropdown(false); }} />
            <div style={{ position: 'fixed', top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width, zIndex: 9999, backgroundColor: themeColors.card, borderRadius: 8, border: `1px solid ${themeColors.cardBorder}`, boxShadow: '0 4px 8px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' as any }}>
              {showLevelDropdown && ['all', 'low', 'medium', 'high', 'critical'].map((level) => (
                <TouchableOpacity key={level} style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterLevel(level); setShowLevelDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterLevel === level && styles.dropdownItemTextActive]}>
                    {level === 'all' ? t('risks.allLevels') : getRiskLevelLabel(level)}
                  </Text>
                </TouchableOpacity>
              ))}
              {showTypeDropdown && ['all', 'strategic', 'operational', 'financial', 'compliance'].map((type) => (
                <TouchableOpacity key={type} style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterType(type); setShowTypeDropdown(false); }}>
                  <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterType === type && styles.dropdownItemTextActive]}>
                    {type === 'all' ? t('risks.allTypes') : getRiskTypeLabel(type)}
                  </Text>
                </TouchableOpacity>
              ))}
              {showGoalDropdown && (
                <>
                  <TouchableOpacity style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterGoal('all'); setShowGoalDropdown(false); }}>
                    <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterGoal === 'all' && styles.dropdownItemTextActive]}>{t('risks.allGoals')}</Text>
                  </TouchableOpacity>
                  {goals.map((goal) => (
                    <TouchableOpacity key={goal.id} style={[styles.dropdownItem, { borderBottomColor: themeColors.border }]} onPress={() => { setFilterGoal(goal.id.toString()); setShowGoalDropdown(false); }}>
                      <Text style={[styles.dropdownItemText, { color: themeColors.textPrimary }, filterGoal === goal.id.toString() && styles.dropdownItemTextActive]} numberOfLines={1}>{translate(goal.title)}</Text>
                    </TouchableOpacity>
                  ))}
                </>
              )}
            </div>
          </>,
          document.body
        )}

        {/* Risk Groups */}
        {riskGroups.length > 0 ? (
          riskGroups.map((group) => (
            <View key={group.key} style={styles.riskGroupSection}>
              {/* Goal Section with Gray Background */}
              <View style={[styles.goalSection, { backgroundColor: themeColors.sectionBg }]}>
                {/* Goal Header */}
                <View style={styles.goalHeader}>
                  {/* Org name badge - shown for strategy view */}
                  {canViewAllOrganizations && group.organizationName && (
                    <View style={styles.orgNameBadge}>
                      <Text style={styles.orgNameBadgeText} numberOfLines={1}>{translateOrg(group.organizationName)}</Text>
                    </View>
                  )}
                  <Text style={[styles.goalTitle, { color: themeColors.textPrimary }]}>{translate(group.goalName)}</Text>
                  <View style={[styles.goalBullet, { backgroundColor: themeColors.textPrimary }]} />
                </View>

                {/* Risk Cards - Horizontal Scroll RTL */}
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.riskCardsScrollContent}
                  style={styles.riskCardsScroll}
                >
                  {/* Cards in RTL order (reverse for RTL scroll) - each card is clickable */}
                  {[...group.risks].reverse().map((risk) => (
                    <TouchableOpacity
                      key={risk.id}
                      style={[styles.riskCard, { width: isSmallScreen ? width - 80 : (width - 340) / 2, backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
                      onPress={() => {
                        navigation.navigate('AddRisk', { riskId: risk.id });
                      }}
                      activeOpacity={0.7}
                    >
                      {/* Risk Code */}
                      {risk.code ? (
                        <Text style={[styles.riskCode, { color: themeColors.textPrimary }]}>{risk.code}</Text>
                      ) : null}

                      {/* Risk Name */}
                      <Text style={[styles.riskName, { color: themeColors.textPrimary }]}>{translate(risk.name)}</Text>

                      {/* Badges Row: Classification + Risk Level */}
                      <View style={styles.badgesRow}>
                        {/* Risk Level Badge with Label - Left side */}
                        <View style={styles.badgeContainer}>
                          <View style={[styles.levelBadge, { backgroundColor: getRiskLevelBadgeColor(risk.risk_level) }]}>
                            <Text style={styles.levelBadgeText}>
                              {getRiskLevelLabel(risk.risk_level)}
                            </Text>
                          </View>
                          <Text style={[styles.badgeLabel, { color: themeColors.textPrimary }]}>{t('risks.riskLevel')}:</Text>
                        </View>

                        {/* Risk Type Badge with Label - Right side */}
                        <View style={styles.badgeContainer}>
                          <View style={[styles.typeBadge, { backgroundColor: getRiskTypeBadgeColor() }]}>
                            <Text style={styles.typeBadgeText}>{getRiskTypeLabel(risk.risk_type)}</Text>
                          </View>
                          <Text style={[styles.badgeLabel, { color: themeColors.textPrimary }]}>{t('risks.classification')}:</Text>
                        </View>
                      </View>

                      {/* Owner + Impact Description Row */}
                      <View style={styles.badgesRow}>
                        {/* Impact Description - Left side */}
                        <View style={styles.impactContainer}>
                          <Text style={[styles.impactText, { color: themeColors.textPrimary }]} numberOfLines={2}>
                            {translate(risk.expected_impact) || '-'}
                          </Text>
                          <Text style={[styles.badgeLabel, { color: themeColors.textPrimary }]}>{t('risks.impactDescription')}:</Text>
                        </View>

                        {/* Owner Badge - Right side */}
                        <View style={styles.badgeContainer}>
                          <View style={styles.ownerBadge}>
                            <Text style={styles.ownerBadgeText} numberOfLines={1}>{translate(risk.risk_owner) || '-'}</Text>
                          </View>
                          <Text style={[styles.badgeLabel, { color: themeColors.textPrimary }]}>{t('risks.owner')}:</Text>
                        </View>
                      </View>

                      {/* Procedures Section */}
                      <View style={styles.proceduresRow}>
                        {/* Treatment Procedures - Left */}
                        <View style={[styles.procedureColumn, { backgroundColor: themeColors.sectionBg }]}>
                          <Text style={[styles.procedureTitle, { color: themeColors.textPrimary }]}>{t('risks.mitigationActions')}</Text>
                          <Text style={[styles.procedureText, { color: themeColors.textPrimary }]}>
                            • {translate(risk.mitigation_actions) || t('risks.currentProceduresSufficient')}
                          </Text>
                        </View>

                        {/* Current Procedures - Right */}
                        <View style={[styles.procedureColumn, { backgroundColor: themeColors.sectionBg }]}>
                          <Text style={[styles.procedureTitle, { color: themeColors.textPrimary }]}>{t('risks.currentAppliedProcedures')}</Text>
                          <Text style={[styles.procedureText, { color: themeColors.textPrimary }]}>
                            • {translate(risk.internal_causes) || t('risks.currentProceduresDefault')}
                          </Text>
                        </View>
                      </View>

                      {/* Edit Icon Row - Below procedures */}
                      {!isViewOnly && (
                        <View style={styles.editIconRow}>
                          <EditIcon size={18} color={themeColors.iconDefault} />
                        </View>
                      )}
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>
          ))
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>{t('risks.noRisksRegistered')}</Text>
          </View>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <View style={styles.pagination}>
            <View style={[styles.paginationButtons, { flexDirection: rtlRow }]}>
              <TouchableOpacity
                style={[styles.paginationBtn, currentPage === 1 && styles.paginationBtnDisabled]}
                onPress={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <Text style={[styles.paginationBtnText, currentPage === 1 && styles.paginationBtnTextDisabled]}>{t('common.previous')}</Text>
                <ChevronRightIcon size={14} color={currentPage === 1 ? '#CBD5E1' : '#64748B'} />
              </TouchableOpacity>

              {/* Page Numbers */}
              {(() => {
                const maxVisible = 5;
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                const end = Math.min(totalPages, start + maxVisible - 1);
                if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                return Array.from({ length: end - start + 1 }, (_, i) => start + i);
              })().map((page) => (
                <TouchableOpacity
                  key={page}
                  style={[styles.paginationPageBtn, currentPage === page && styles.paginationPageBtnActive]}
                  onPress={() => setCurrentPage(page)}
                >
                  <Text style={[styles.paginationPageText, currentPage === page && styles.paginationPageTextActive]}>
                    {page}
                  </Text>
                </TouchableOpacity>
              ))}

              <TouchableOpacity
                style={[styles.paginationBtn, currentPage === totalPages && styles.paginationBtnDisabled]}
                onPress={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronLeftIcon size={14} color={currentPage === totalPages ? '#CBD5E1' : '#64748B'} />
                <Text style={[styles.paginationBtnText, currentPage === totalPages && styles.paginationBtnTextDisabled]}>{t('common.next')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  // Toast Notification
  toast: {
    position: 'absolute',
    top: 80,
    left: '50%',
    transform: [{ translateX: -150 }],
    width: 300,
    backgroundColor: '#22C55E',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    zIndex: 1000,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 10,
  },
  toastText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#737373',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#D4D4D4',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },
  // Title Row
  headerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginBottom: 24,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
  },
  filterRowWrapper: {
    zIndex: 100,
    position: 'relative' as any,
  },
  headerRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  orgFilterRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginTop: 12,
    zIndex: 200,
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#A9211F',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  // Filter Row
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 16,
    marginTop: 16,
    position: 'relative',
    zIndex: 1000,
  },
  applyFilterBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
  },
  applyFilterBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },
  clearFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FCA5A5',
    backgroundColor: '#FEF2F2',
    minHeight: 44,
    gap: 6,
  },
  clearFilterText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#EF4444',
  },
  filterDropdownContainer: {
    flex: 1,
    maxWidth: 200,
    position: 'relative',
    overflow: 'visible' as any,
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
  },
  filterDropdownText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownOverlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  },
  dropdownMenuFixed: {
    position: 'fixed' as any,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 10,
    zIndex: 9999,
  },
  dropdownItem: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownItemTextActive: {
    color: COLORS.primary,
    fontFamily: FONTS.medium,
  },
  // Risk Group Section
  riskGroupSection: {
    marginBottom: 24,
    position: 'relative' as any,
    zIndex: 1,
  },
  goalSection: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  goalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    paddingHorizontal: 8,
  },
  goalBullet: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#414042',
  },
  orgNameBadge: {
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  orgNameBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  goalTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    flex: 1,
  },
  // Risk Cards - Horizontal Scroll
  riskCardsScroll: {
    flexDirection: 'row-reverse', // RTL scroll direction
  },
  riskCardsScrollContent: {
    flexDirection: 'row-reverse', // RTL content direction
    paddingHorizontal: 0,
    gap: 20,
  },
  riskCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    padding: 12,
    gap: 16,
    position: 'relative',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    minWidth: 300,
    maxWidth: 500,
    overflow: 'hidden',
  },
  riskCode: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  riskName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 16,
  },
  badgeContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  badgeLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    minHeight: 24,
    justifyContent: 'center',
  },
  typeBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#F8FAFC',
    textAlign: 'center',
  },
  levelBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    minHeight: 24,
    justifyContent: 'center',
  },
  levelBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#F8FAFC',
    textAlign: 'center',
  },
  // Owner badge (gold pill)
  ownerBadge: {
    flex: 1,
    backgroundColor: '#A6813F',
    borderRadius: 40,
    paddingHorizontal: 8,
    paddingVertical: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  ownerBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  // Impact description
  impactContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: 4,
    paddingVertical: 4,
  },
  impactText: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 14,
  },
  // Procedures
  proceduresRow: {
    flexDirection: 'row',
    gap: 16,
  },
  procedureColumn: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    gap: 4,
  },
  procedureTitle: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    height: 20,
    lineHeight: 20,
  },
  procedureText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 14,
  },
  // Edit Icon Row
  editIconRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    paddingTop: 4,
  },
  // Empty State
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'center',
  },
  // Pagination
  pagination: {
    marginTop: 24,
    alignItems: 'center',
  },
  paginationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paginationBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  paginationBtnDisabled: {
    opacity: 0.5,
  },
  paginationBtnText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#334155',
  },
  paginationBtnTextDisabled: {
    color: '#CBD5E1',
  },
  paginationPageBtn: {
    minWidth: 36,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
  },
  paginationPageBtnActive: {
    borderColor: '#334155',
    backgroundColor: '#FFFFFF',
  },
  paginationPageText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#334155',
  },
  paginationPageTextActive: {
    color: '#334155',
  },
});
